import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ForbiddenState, LoadingState } from '../../components/PageState';
import { useAuth } from './AuthProvider';

/** Sends signed-out visitors to the login page and remembers where they were going. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <LoadingState />;
  if (status === 'signedOut')
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

/**
 * Hides a page from users whose group lacks the permission. This is a convenience only:
 * the API enforces the same code on every request.
 */
export function RequirePermission({
  code,
  children,
}: {
  code: string | string[];
  children: ReactNode;
}) {
  const { can } = useAuth();
  const codes = Array.isArray(code) ? code : [code];
  if (!codes.some(can)) return <ForbiddenState code={codes.join(' / ')} />;
  return <>{children}</>;
}

/** Renders children only when the user holds the permission. For buttons and menu items. */
export function Can({ code, children }: { code: string; children: ReactNode }) {
  const { can } = useAuth();
  return can(code) ? <>{children}</> : null;
}
